import { type FC, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import {
  EnrollmentInvestmentExperienceCategory,
  EnrollmentInvestmentObjectiveCategory,
  useGetEnrollmentInvestmentGoalsData,
  useSubmitEnrollmentInvestmentGoalsData,
} from '@egw/api'
import { Button, GenericError, RadioGroup } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { ROUTES } from '../../../constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Investment Goals')

interface FormData {
  objective: string
  experience: string
}

export const InvestmentGoalsPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentInvestmentGoalsData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentInvestmentGoalsData()

  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm({})

  useEffect(() => {
    if (data?.enrollmentData.userInfo.investment.experience !== undefined) {
      reset({
        experience: data.enrollmentData.userInfo.investment.experience,
        objective: data.enrollmentData.userInfo.investment.objective,
      })
    }
  }, [data?.enrollmentData.userInfo])

  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData

    try {
      const response = await mutate({
        investmentProfile: {
          objective: formData.objective as EnrollmentInvestmentObjectiveCategory,
          experience: formData.experience as EnrollmentInvestmentExperienceCategory,
        },
      })
      if (response?.path) {
        navigate(response.path)
      } else {
        // TODO: Handle the case when path is not returned
        navigate(ROUTES.HOME.path)
      }
    } catch (e) {
      log.error('Error submitting email', e)
    }
  })

  return (
    <EnrollmentLayout title="Investment Goals">
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <PageHeader title={t('investments.investment-goals.title')} />
            <p className="typo-primary-sm-regular text-text-meta">{t('investments.investment-goals.description')}</p>
            <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col h-full">
              <p className="typo-primary-base-semibold my-md">{t('investments.investment-goals.objective_title')}</p>
              <Controller
                name="objective"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { ref, ...restField } }) => (
                  <RadioGroup
                    {...restField}
                    options={Object.values(EnrollmentInvestmentObjectiveCategory).map((item) => ({
                      value: item,
                      label: t(`investments.investment-goals.investment_objective.${item}`),
                    }))}
                    name="objective"
                    className="pb-md"
                    testId="objective-radio"
                  />
                )}
              />

              <p className="typo-primary-base-semibold my-md">{t('investments.investment-goals.experience_title')}</p>
              <Controller
                name="experience"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { ref, ...restField } }) => (
                  <RadioGroup
                    {...restField}
                    options={Object.values(EnrollmentInvestmentExperienceCategory).map((item) => ({
                      value: item,
                      label: t(`investments.investment-goals.investment_experience.${item}`),
                    }))}
                    name="experience"
                    className="pb-md"
                    testId="experience-radio"
                  />
                )}
              />

              <div className="mt-auto md:mt-lg">
                <Button
                  type="submit"
                  className="w-full md:w-min mt-lg"
                  isDisabled={!isValid}
                  isLoading={isSubmitting}
                  testId="enrollment-continue-button"
                >
                  {t('common.continue')}
                </Button>
              </div>
            </form>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
